import { createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { configurationOptionSchema } from '@/models/configurationOptionSchema';
import { miriamTypeSchema } from '@/models/miriamTypeSchema';
import { mapFormatSchema } from '@/models/mapFormatSchema';
import { imageFormatSchema } from '@/models/imageFormatSchema';
import { statusSchema } from '@/models/statusSchema';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { Configuration, ConfigurationOption } from '@/types/configuration';
import { apiPath } from '../apiPath';
import {
  CONFIGURATION_FETCHING_ERROR_PREFIX,
  CONFIGURATION_OPTIONS_FETCHING_ERROR_PREFIX,
} from './configuration.constants';

export const getConfigurationOptions = createAsyncThunk<
  ConfigurationOption[] | undefined,
  void,
  ThunkConfig
>('configuration/getConfigurationOptions', async () => {
  try {
    const response = await axiosInstanceNewAPI.get<ConfigurationOption[]>(
      apiPath.getConfigurationOptions(),
    );

    const isDataValid = validateDataUsingZodSchema(
      response.data,
      z.array(configurationOptionSchema),
    );

    return isDataValid ? response.data : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: CONFIGURATION_OPTIONS_FETCHING_ERROR_PREFIX }));
  }
});

export const getConfiguration = createAsyncThunk<Configuration | undefined, void, ThunkConfig>(
  'configuration/getConfiguration',
  async () => {
    try {
      const [
        statusResponse,
        miriamTypesResponse,
        mapFormatsResponse,
        imageFormatsResponse,
        optionsResponse,
      ] = await Promise.all([
        axiosInstanceNewAPI.get(apiPath.getStatus()),
        axiosInstanceNewAPI.get(apiPath.getMiriamTypes()),
        axiosInstanceNewAPI.get(apiPath.getMapFormats()),
        axiosInstanceNewAPI.get(apiPath.getImageFormats()),
        axiosInstanceNewAPI.get(apiPath.getConfigurationOptions()),
      ]);

      const isDataValid =
        validateDataUsingZodSchema(statusResponse.data, statusSchema) &&
        validateDataUsingZodSchema(miriamTypesResponse.data, z.array(miriamTypeSchema)) &&
        validateDataUsingZodSchema(mapFormatsResponse.data, z.array(mapFormatSchema)) &&
        validateDataUsingZodSchema(imageFormatsResponse.data, z.array(imageFormatSchema)) &&
        validateDataUsingZodSchema(optionsResponse.data, z.array(configurationOptionSchema));

      if (!isDataValid) return undefined;

      return {
        version: statusResponse.data.version,
        buildDate: statusResponse.data.buildTime,
        gitHash: statusResponse.data.gitHash,
        miriamTypes: miriamTypesResponse.data,
        mapFormats: mapFormatsResponse.data,
        imageFormats: imageFormatsResponse.data,
        options: optionsResponse.data,
      };
    } catch (error) {
      return Promise.reject(getError({ error, prefix: CONFIGURATION_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const updateConfiguration = createAsyncThunk<
  boolean,
  { id: string; value: string },
  ThunkConfig
>('configuration/updateConfiguration', async ({ id, value }) => {
  try {
    await axiosInstanceNewAPI.put(
      apiPath.updateConfiguration(id),
      { value },
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return true;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Failed to update plugin' }));
  }
});

export const registerMachine = createAsyncThunk<boolean, void, ThunkConfig>(
  'minervanet/registerMachine',
  async () => {
    try {
      await axiosInstanceNewAPI.post(apiPath.registerMachine());
      return true;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to register machine' }));
    }
  },
);

export const unRegisterMachine = createAsyncThunk<boolean, void, ThunkConfig>(
  'minervanet/unRegisterMachine',
  async () => {
    try {
      await axiosInstanceNewAPI.post(apiPath.unregisterMachine());
      return true;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to unregister machine' }));
    }
  },
);
